/**
 * <next-tier-progress> und <tier-price-header>
 * Zeigen Staffelpreis-Infos an, die live aktualisiert werden, wenn der
 * Mengenwähler (quantity-input) geändert wird.
 * Datenquelle (beide Custom Elements, per data-tiers): entweder
 * product.metafields.custom.staffelpreise (B2C) oder die nativen
 * quantity_price_breaks (B2B/Fachkreis-Katalog).
 * Format: "Menge:Preis_in_Cent,Menge:Preis_in_Cent,...".
 *
 * Diese Datei kann auf derselben Seite mehrfach eingebunden werden (z. B.
 * einmal aus tier-price-header.liquid und einmal aus next-tier-progress(-b2b)
 * .liquid) - das IIFE-Guard unten sorgt dafür, dass die Custom Elements nur
 * beim ersten Durchlauf registriert werden.
 */
(function () {
  if (window.__nextTierProgressLoaded) {
    return;
  }
  window.__nextTierProgressLoaded = true;

  function parseTiers(raw) {
    if (!raw) return [];
    return raw
      .split(',')
      .map((part) => {
        const [qty, price] = part.split(':');
        return { qty: parseInt(qty, 10), price: parseInt(price, 10) };
      })
      .filter((tier) => !isNaN(tier.qty) && !isNaN(tier.price) && tier.qty > 0)
      .sort((a, b) => a.qty - b.qty);
  }

  function formatMoney(cents) {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(cents / 100);
  }

  class NextTierProgress extends HTMLElement {
    connectedCallback() {
      this.textEl = this.querySelector('.next-tier-progress__text');
      this.fillEl = this.querySelector('.next-tier-progress__bar-fill');
      this.basePrice = parseInt(this.dataset.basePrice, 10) || 0;
      this.tiers = parseTiers(this.dataset.tiers);

      if (!this.tiers.length) {
        this.hidden = true;
        return;
      }

      const inputId = this.dataset.quantityInputId;
      this.input = inputId ? document.getElementById(inputId) : null;

      if (this.input) {
        this.boundUpdate = this.update.bind(this);
        this.input.addEventListener('change', this.boundUpdate);
        this.input.addEventListener('input', this.boundUpdate);
      }

      this.update();
    }

    disconnectedCallback() {
      if (this.input && this.boundUpdate) {
        this.input.removeEventListener('change', this.boundUpdate);
        this.input.removeEventListener('input', this.boundUpdate);
      }
    }

    currentQuantity() {
      if (!this.input) return 1;
      const value = parseInt(this.input.value, 10);
      return isNaN(value) || value < 1 ? 1 : value;
    }

    update() {
      const qty = this.currentQuantity();
      const nextTier = this.tiers.find((tier) => qty < tier.qty);
      const reachedTiers = this.tiers.filter((tier) => qty >= tier.qty);
      const currentTier = reachedTiers.length
        ? reachedTiers[reachedTiers.length - 1]
        : null;

      if (nextTier) {
        const missing = nextTier.qty - qty;
        const percentOff =
          this.basePrice > 0
            ? Math.round((1 - nextTier.price / this.basePrice) * 100)
            : null;
        const percentLabel = percentOff ? ` (-${percentOff}%)` : '';
        const unit = missing === 1 ? 'Stück' : 'Stück';

        this.textEl.innerHTML =
          `Noch <strong>${missing}</strong> ${unit} bis ${formatMoney(nextTier.price)}/Stück${percentLabel}`;

        const rangeStart = currentTier ? currentTier.qty : 0;
        const progress = (qty - rangeStart) / (nextTier.qty - rangeStart);

        this.fillEl.style.width = `${Math.min(Math.max(progress, 0), 1) * 100}%`;
        this.classList.remove('is-max-tier');
        this.hidden = false;
      } else if (currentTier) {
        this.textEl.innerHTML = `Bester Preis erreicht: ${formatMoney(currentTier.price)}/Stück`;
        this.fillEl.style.width = '100%';
        this.classList.add('is-max-tier');
        this.hidden = false;
      } else {
        this.hidden = true;
      }
    }
  }

  class TierPriceHeader extends HTMLElement {
    connectedCallback() {
      this.compareEl = this.querySelector('.tier-price-header__compare');
      this.currentEl = this.querySelector('.tier-price-header__current');
      this.labelEl = this.querySelector('.tier-price-header__label');
      this.baseCents = parseInt(this.dataset.basePrice, 10) || 0;
      const compareAttr = parseInt(this.dataset.comparePrice, 10);
      this.compareCents = isNaN(compareAttr) || compareAttr <= 0 ? this.baseCents : compareAttr;
      this.tiers = parseTiers(this.dataset.tiers);

      const inputId = this.dataset.quantityInputId;
      this.input = inputId ? document.getElementById(inputId) : null;

      if (this.input) {
        this.boundUpdate = this.update.bind(this);
        this.input.addEventListener('change', this.boundUpdate);
        this.input.addEventListener('input', this.boundUpdate);
      }

      this.update();
    }

    disconnectedCallback() {
      if (this.input && this.boundUpdate) {
        this.input.removeEventListener('change', this.boundUpdate);
        this.input.removeEventListener('input', this.boundUpdate);
      }
    }

    currentQuantity() {
      if (!this.input) return 1;
      const value = parseInt(this.input.value, 10);
      return isNaN(value) || value < 1 ? 1 : value;
    }

    update() {
      const qty = this.currentQuantity();
      const reachedTiers = this.tiers.filter((tier) => qty >= tier.qty);
      const currentTier = reachedTiers.length
        ? reachedTiers[reachedTiers.length - 1]
        : null;

      const activeCents = currentTier ? currentTier.price : this.baseCents;

      if (this.compareCents > activeCents) {
        this.compareEl.textContent = formatMoney(this.compareCents);
        this.compareEl.hidden = false;
      } else {
        this.compareEl.hidden = true;
      }

      this.currentEl.textContent = formatMoney(activeCents);

      if (currentTier) {
        this.labelEl.textContent = `ab ${currentTier.qty} Stück`;
        this.labelEl.hidden = false;
      } else {
        this.labelEl.hidden = true;
      }
    }
  }

  customElements.define('next-tier-progress', NextTierProgress);
  customElements.define('tier-price-header', TierPriceHeader);
})();
